import devDebug from "../../../../Helpers/debug";
import i18n from "../../../../i18n";
import { useTaskManagementStore } from "../../../../Stores/taskManagementStore";
import { useViewEditTenderStore } from "../../../../Stores/viewEditTender";
import { checkProgress, updateTaskStatus } from "../../2_UtilityFunctions/viewTenderUtilities";
import { MeiliSearch } from "meilisearch";
import { cloneDeep } from "lodash";

export const defaultAdditional = {
  offset: 0,
};

export const loadPageOptionsMeilisearch = async (q, prevOptions, { offset }) => {
  const meilisearchIndex = useViewEditTenderStore.getState().meilisearchIndex;
  const limit = 10;

  let json;
  try {
    json = await meilisearchIndex.search(q, {
      limit: limit,
      offset: offset,
      showRankingScore: true,
      rankingScoreThreshold: 0.6,
    });
  } catch (e) {
    devDebug("!!!! Edit Tender -> TA Search -> Error: " + e);
    // handle fetch error
    return {
      options: [],
      hasMore: false,
    };
  }

  // handle unexpected json
  if (!json.hits) {
    devDebug("!!!! Edit Tender -> TA Search -> There was no json.hits field");
    return {
      options: [],
      hasMore: false,
    };
  }

  return {
    options: json.hits.map((ta) => ({
      value: ta.id,
      nut_code: ta.nut_code,
      label: `${ta.name}`,
      ta: ta,
    })),
    hasMore: json.hits < 10 ? false : true,

    additional: {
      offset: offset + limit,
    },
  };
};

export const loadTenderingAuthorityOptions = (q, auth) => {
  // Returns a Promise so it's compatible with AsyncPaginate's loadOptions prop.
  return new Promise((resolve) => {
    const limit = 10;
    const controller = new AbortController();

    auth.apiService.axiosGET(
      `/UAadmin/tendering-authority/search/?q=${encodeURIComponent(q)}&top_k=${limit}`,
      controller.signal,
      // success callback:
      (response) => {
        // Handle both array or {results: [...]}
        const results = Array.isArray(response) ? response : response.results || [];
        resolve({
          options: results.map((ta) => ({
            value: ta.id,
            nut_code: ta.nut_code,
            label: ta.name,
            ta: ta,
            ranking_score: ta.ranking_score,
          })),
        });
      },
      // error alerter (can be a UI alert, logger, etc.):
      (errorMessage) => {
        // Optionally show a user notification
      },
      // static error message for generic failures:
      "Failed to load tendering authorities",
      // error handler:
      (error) => {
        // On error, return no options
        resolve({
          options: [],
          hasMore: false,
          additional: {},
        });
      }
    );
  });
};

export const getMeilisearchTokenSuccess = (resp) => {
  useViewEditTenderStore.getState().setMeilisearchToken(resp.meilisearch_token);
  useViewEditTenderStore.getState().setMeilisearchTokenStatus("loaded");

  const client = new MeiliSearch({
    host: import.meta.env.VITE_MEILISEARCH_URL,
    apiKey: resp.meilisearch_token,
  });

  const index = client.index(import.meta.env.VITE_MEILISEARCH_INDEX_TAS);

  useViewEditTenderStore.getState().setMeilisearchIndex(index);
};

export const getMeilisearchTokenError = () => {
  useViewEditTenderStore.getState().setMeilisearchTokenStatus("error");
};

export const getMeilisearchToken = (auth) => {
  const controller = new AbortController();

  auth.apiService.axiosGET(
    `common/third-party/auth/tokens/`, //endpoint
    controller.signal, //signal
    getMeilisearchTokenSuccess, //successFn
    auth.newErrorHandler, //error AlerterFn
    "There was an error getting the meilisearch token", //error message
    getMeilisearchTokenError, //errorFn (gets error back)
    {
      section: "Edit Tender -> Search Tendering Authorities -> getMeilisearchToken",
    } //data (gets handed back to success fn)
  );
};

export const getExtraFieldsViaAISuccess = (resp) => {
  useViewEditTenderStore.getState().setExtraFieldsAIResult(resp);
};

export const getExtraFieldsViaAIError = () => {
  useViewEditTenderStore.getState().setMeilisearchTokenStatus("error");
};

export const getExtraFieldsViaAI = (auth, id) => {
  const controller = new AbortController();

  auth.apiService.axiosPOST(
    `ai/populate-extras/`, //endpoint
    controller.signal, //signal
    getExtraFieldsViaAISuccess, //successFn
    auth.newErrorHandler, //error AlerterFn
    "There was an error regenerating the extra fields", //error message
    getExtraFieldsViaAIError, //errorFn (gets error back)
    {
      section: "Edit Tender -> Regenerate extra fields",
      payload: {
        tender: id,
        run_async: false, //Whether to run as a background task or not
        regenerate: !useViewEditTenderStore.getState().regenerateEmptyOnly, //Whether to override existing extras or not
      },
    } //data (gets handed back to success fn)
  );
};
export const getGeneratableFieldsSuccess = (resp) => {
  let newResults = cloneDeep(resp.results);
  for (let field of newResults) {
    switch (
      field.field_name //todo: work with backend to standardise these names, this conversion is brittle at best
    ) {
      case "procedure_typee":
        field.field_name = "procedure_type";
        break;
      case "award_criteriaa":
        field.field_name = "award_criteria";
        break;
      case "soacategory_set":
        field.field_name = "soa_categories";
        break;
      default:
      // devDebug("getGeneratableFieldsSuccess -> Didn't need to check this field: ", field.field_name);
    }
  }

  newResults.push({ field_name: "currency" });
  useViewEditTenderStore.getState().setGeneratableFields(newResults || []);
};

export const getGeneratableFieldsError = () => {
  devDebug("There was an error getting the generatable fields");
};

export const getGeneratableFields = (auth) => {
  const controller = new AbortController();

  auth.apiService.axiosGET(
    `ai/generatable-fields/?page_size=max`, //endpoint
    controller.signal, //signal
    getGeneratableFieldsSuccess, //successFn
    auth.newErrorHandler, //error AlerterFn
    "There was an error getting the generatable fields", //error message
    getGeneratableFieldsError, //errorFn (gets error back)
    {
      section: "Edit Tender -> Get generatable fields",
    } //data (gets handed back to success fn)
  );
};

export const generateFieldSuccess = (resp, data) => {
  devDebug("This is response from editTenderFieldRegenerate button: ", resp);
  data.auth.show({
    message: i18n.t("Regenerating field"),
    type: "alert_success",
  });
  useTaskManagementStore.getState().setTask("editTenderFieldRegenerate", data.id, resp, data.fieldName); //resp not resp.task here, as this endpoint doesn't return a task (which is a different structure from the other ai endpoint
  setTimeout(() => {
    if (!useTaskManagementStore.getState()?.intervals?.editTenderFieldRegenerate?.[data?.id]?.[data.fieldName]) {
      let interval = setInterval(() => {
        devDebug("!!!!!!!  interval from view Tender generateFieldSuccess");
        checkProgress(
          //auth, type, taskID, objectID, successFN, typeOfTask, searchTenderPayload, fieldName
          data.auth,
          "editTenderFieldRegenerate",
          resp.task_id,
          data.id,
          updateTaskStatus,
          "editRegen",
          {}, //only used when it's auto-confirm on search tender
          data.fieldName
        );
      }, 2000);
      useTaskManagementStore.getState().setTaskInterval("editTenderFieldRegenerate", data.id, interval, data.fieldName); //fieldName is only used when on edit_tender, regenerating fields
    }
  }, 1000);
};

export const generateFieldError = (err) => {
  devDebug("There was an error regenerating the tender's field: ", err);
};

export const generateField = (auth, fieldName, originalFieldName = fieldName) => {
  const tenderID = useViewEditTenderStore.getState().tender.id;

  const controller = new AbortController();

  auth.apiService.axiosPOST(
    `/ai/generate-field/`, //endpoint
    controller.signal, //signal
    generateFieldSuccess, //successFn
    auth.newErrorHandler, //error AlerterFn
    "There was an error regenerating the tender's field", //error message
    generateFieldError, //errorFn (gets error back)
    {
      id: tenderID,
      fieldName: originalFieldName,
      apiFieldName: fieldName,
      auth: auth,
      payload: { tender_id: tenderID, field_name: fieldName },
      section: "Edit Tender -> regenerate",
    } //data (gets handed back to success fn)
  );
};

export function getQueryHistoryEditTender(auth) {
  const tenderID = useViewEditTenderStore.getState().tender.id;
  //TODO: this needs to change to filter by a specific field
  const controller = new AbortController();
  auth.apiService.axiosGET(
    `ai/query-history/?object_id=${tenderID}&page_size=max`, //endpoint
    controller.signal, //signal
    getQueryHistorySuccess, //successFn
    auth.newErrorHandler, //error AlerterFn
    "There was an error getting the query history", //error message
    getQueryHistoryError, //errorFn (gets error back)
    {
      tender_id: tenderID,
      auth: auth,
      section: "Summaries -> get query history",
    } //data (gets handed back to success fn)
  );
}

const getQueryHistorySuccess = (resp, data) => {
  useViewEditTenderStore.getState().setQueryHistoryItems(resp.results);
};
const getQueryHistoryError = (err, data) => {
  devDebug("There was an error getting query history: ", err);
};
